<script setup lang="ts">
import { ref } from 'vue';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/Spinner.vue';
import { defaultReportFarmIds, type ReportDateSelection } from '@/composables/inventory/inventoryReport';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { useMedicineReport } from '@/plugins/store/actions/queries/inventory';
import type { MedicineReportRequestFarm } from '@/types';
import ReportResult from './ReportResult.vue';
import SelectDatesStep from './SelectDatesStep.vue';
import SelectFarmsStep from './SelectFarmsStep.vue';

type Step = 'farms' | 'dates' | 'report';

const step = ref<Step>('farms');
const { selectedFarms } = useInventoryScope();
/** The first farms of the dashboard's selection are ticked when the wizard starts. */
const farmIds = ref<number[]>(defaultReportFarmIds(selectedFarms.value));
const dateSelection = ref<ReportDateSelection>({});

const { mutate: loadReport, reset: resetReport, data: report, isPending, isError } = useMedicineReport();

/** Asks for the report of the farms and days the steps collected. */
function show(farms: MedicineReportRequestFarm[]) {
    step.value = 'report';
    loadReport(farms);
}

function startOver() {
    step.value = 'farms';
    // Back to the same first farms a fresh wizard starts with, not the last report's.
    farmIds.value = defaultReportFarmIds(selectedFarms.value);
    dateSelection.value = {};
    resetReport();
}
</script>

<template>
    <div>
        <div class="flex justify-center">
            <MainViewCard v-if="step !== 'report'" class="max-w-4xl w-full px-4 py-4">
                <SelectFarmsStep v-if="step === 'farms'" v-model:value="farmIds" @next="step = 'dates'" />
                <SelectDatesStep v-else v-model:value="dateSelection" :farm-ids="farmIds" @back="step = 'farms'" @next="show" />
            </MainViewCard>
            <div v-else class="w-full">
                <div v-if="isPending" class="flex flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white p-16">
                    <Spinner class="h-10 w-10 animate-spin" />
                    <span class="text-sm text-gray-500">{{ $t('views.health.inventory.report.computing') }}</span>
                </div>
                <div v-else-if="isError" class="flex flex-col items-center justify-center gap-4 rounded-lg border border-gray-200 bg-white p-16">
                    <p class="text-sm text-red-600">{{ $t('views.health.inventory.report.reportError') }}</p>
                    <Button variant="outline" @click="startOver">{{ $t('views.health.inventory.report.startOver') }}</Button>
                </div>
                <ReportResult v-else-if="report" :farms="report" @reset="startOver" />
            </div>
        </div>
    </div>
</template>
